import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CalendarClock, ListChecks, Plus } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { BotaoLixeira } from '../../componentes/lixeira';
import { Botao, Cartao, Selo } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import { Selecao } from '../../componentes/formulario';
import { Kanban as KanbanNucleo } from '../../componentes/kanban';
import { Drawer } from '../../componentes/sobreposicao';
import { TextoCortado } from '../../componentes/texto-cortado';
import { SeloVerticais, useEmpresasDoCatalogo } from '../../componentes/verticais';
import { useToast } from '../../componentes/toast';
import { get, mensagemDeErro, post } from '../../nucleo/api';
import { data, numero, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import {
  CHAVE,
  COR_PRIORIDADE,
  SeletorQuadro,
  SelosColuna,
  atravessaAprovacao,
  useCardAberto,
  useOpcoesModuloAlvo,
  useQuadroEscolhido,
  useQuadros,
  useRotuloColuna,
  useRotuloModuloAlvo,
  useTempoRealTarefas,
} from './comum';
import { DrawerCard } from './DrawerCard';
import { FormCard } from './FormCard';
import { PRIORIDADES, TIPOS, type Card, type Kanban } from './tipos';

function CartaoCard({ c }: { c: Card }) {
  const { t } = useTranslation('tarefas');
  const rotuloAlvo = useRotuloModuloAlvo();
  // 22/10 (Gustavo): a vertical do card (o contrato do ganho nasce nela), como no card do Pipeline.
  const catalogo = useEmpresasDoCatalogo().data?.empresas ?? [];
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-start justify-between gap-2">
        <span className="numero text-xs text-tinta-3">{`#${c.numero}`}</span>
        <Selo cor={COR_PRIORIDADE[c.prioridade] ?? 'neutro'}>{c.prioridade}</Selo>
      </div>
      <p className="leading-snug font-medium break-words">{textoLivre(c.titulo)}</p>
      <div className="flex flex-wrap gap-1">
        {c.tipo && <Selo cor="ciano">{c.tipo}</Selo>}
        {c.tamanho && <Selo>{t(`tamanhos.${c.tamanho}`)}</Selo>}
        {c.modulo_alvo && <Selo cor="roxo">{rotuloAlvo(c.modulo_alvo)}</Selo>}
        <SeloVerticais empresas={c.empresas} catalogo={catalogo} />
      </div>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-tinta-3">
        <TextoCortado className="">{c.responsavel_nome ?? t('origens.' + c.origem)}</TextoCortado>
        {c.prazo && (
          <span className={`inline-flex items-center gap-1 ${c.atrasado ? 'text-perigo' : ''}`}>
            <CalendarClock className="size-3.5" aria-hidden />
            <span className="numero">{data(c.prazo)}</span>
            {c.atrasado && <span className="sr-only">{t('kanban.atrasado')}</span>}
          </span>
        )}
        {c.criterios_total > 0 && (
          <span className="inline-flex items-center gap-1" title={t('kanban.criterios', { feitos: c.criterios_feitos, total: c.criterios_total })}>
            <ListChecks className="size-3.5" aria-hidden />
            <span className="numero">{`${c.criterios_feitos}/${c.criterios_total}`}</span>
          </span>
        )}
      </div>
    </div>
  );
}

/** Aba Kanban: o padrão do kanban do Pipeline (arrastar com mouse, toque e teclado), com WIP e colunas de aprovação. */
export function QuadroKanban() {
  const { t } = useTranslation('tarefas');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  useTempoRealTarefas();
  const rotuloColuna = useRotuloColuna();
  const alvos = useOpcoesModuloAlvo();
  const quadros = useQuadros();
  const [quadroId, escolherQuadro] = useQuadroEscolhido(quadros.data?.itens);
  const [cardAberto, abrir] = useCardAberto();
  const [soMinhas, setSoMinhas] = useState(false);
  const [tipo, setTipo] = useState('');
  const [prioridade, setPrioridade] = useState('');
  const [alvo, setAlvo] = useState('');
  const [novo, setNovo] = useState(false);
  const [chave, setChave] = useState(0);
  const filtros = { responsavel_id: soMinhas ? s.usuario.id : undefined, tipo: tipo || undefined, prioridade: prioridade || undefined, modulo_alvo: alvo || undefined };
  const q = useQuery({
    queryKey: [...CHAVE, 'kanban', quadroId, filtros],
    queryFn: () => get<Kanban>(`/tarefas/quadros/${quadroId}/kanban`, filtros),
    enabled: !!quadroId,
  });
  const remontar = () => setChave((k) => k + 1);
  const recarregar = () => qc.invalidateQueries({ queryKey: CHAVE });
  const mover = useMutation({
    mutationFn: (v: { id: string; coluna_id: string }) => post(`/tarefas/cards/${v.id}/mover`, { coluna_id: v.coluna_id }),
    onSuccess: recarregar,
    onError: async (e) => {
      toast.erro(mensagemDeErro(e));
      remontar();
      await recarregar();
    },
  });
  const quadro = quadros.data?.itens.find((x) => x.id === quadroId);
  const temFiltro = soMinhas || !!tipo || !!prioridade || !!alvo;

  return (
    <>
      {s.pode('tarefas.criar') && quadro && (
        <AcaoPrimaria>
          {s.pode('tarefas.excluir') && <BotaoLixeira entidades={['tarefas.card']} />}
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setNovo(true)}>
            {t('acoes.novoCard')}
          </Botao>
        </AcaoPrimaria>
      )}
      <Estado consulta={quadros}>
        {(lq) =>
          !lq.itens.length ? (
            <Cartao semPadding>
              <Vazio texto={t('kanban.semQuadro')} />
            </Cartao>
          ) : (
            <>
              <div role="group" aria-label={t('kanban.filtros')} className="mb-4 flex flex-wrap items-end gap-2">
                <SeletorQuadro quadros={lq.itens} valor={quadroId} aoMudar={escolherQuadro} />
                <div className="w-full sm:w-44">
                  <Selecao aria-label={t('campos.tipo')} opcoes={TIPOS.map((x) => ({ valor: x, rotulo: `${x} · ${t(`tipos.${x}`)}` }))} vazio={`${t('campos.tipo')}: ${t('kanban.todos')}`} value={tipo} onChange={(e) => setTipo(e.target.value)} />
                </div>
                <div className="w-full sm:w-44">
                  <Selecao aria-label={t('campos.prioridade')} opcoes={PRIORIDADES.map((x) => ({ valor: x, rotulo: t(`prioridades.${x}`) }))} vazio={`${t('campos.prioridade')}: ${t('kanban.todas')}`} value={prioridade} onChange={(e) => setPrioridade(e.target.value)} />
                </div>
                <div className="w-full sm:w-48">
                  <Selecao aria-label={t('campos.moduloAlvo')} opcoes={alvos} vazio={`${t('campos.moduloAlvo')}: ${t('kanban.todos')}`} value={alvo} onChange={(e) => setAlvo(e.target.value)} />
                </div>
                <Botao variante="secundario" className={soMinhas ? 'border-2 border-acento-forte text-acento-forte' : ''} aria-pressed={soMinhas} onClick={() => setSoMinhas((v) => !v)}>
                  {t('kanban.soMinhas')}
                </Botao>
              </div>
              <p className="mb-3 text-xs text-tinta-3">{t('kanban.teclado')}</p>
              <Estado consulta={q}>
                {(d) => (
                  <>
                    {d.truncado && (
                      <p role="status" className="mb-3 rounded-lg border border-laranja px-3 py-2 text-sm text-tinta-2">
                        {t('kanban.truncado')}
                      </p>
                    )}
                    {d.cards.length === 0 ? (
                      <Cartao semPadding>
                        <Vazio titulo={temFiltro ? undefined : t('kanban.vazioTitulo')} texto={temFiltro ? t('kanban.vazioFiltro') : t('kanban.vazioTexto')} />
                      </Cartao>
                    ) : (
                      <KanbanNucleo<Card>
                        key={chave}
                        colunas={d.colunas.map((c) => {
                          const cheia = c.limite_wip !== null && c.total >= c.limite_wip;
                          return {
                            id: c.id,
                            titulo: rotuloColuna(c),
                            rodape: (
                              <span className="flex flex-col gap-1.5">
                                <span className={`block ${cheia ? 'font-semibold text-laranja' : ''}`}>
                                  {c.limite_wip !== null ? t('kanban.wip', { total: numero(c.total), limite: numero(c.limite_wip) }) : t('kanban.semLimite', { count: c.total, total: numero(c.total) })}
                                  {cheia && <span className="sr-only">{`. ${t('kanban.wipCheio')}`}</span>}
                                </span>
                                {c.etapa === 'concluida' && <span className="block">{t('kanban.concluidosRecentes')}</span>}
                                {(c.exibidos ?? 0) < (c.filtrados ?? 0) && <span className="block">{t('kanban.exibindo', { n: numero(c.exibidos ?? 0) })}</span>}
                                {(c.exige_aprovacao || c.espera_pessoa) && (
                                  <span className="flex flex-wrap gap-1">
                                    <SelosColuna c={c} />
                                  </span>
                                )}
                              </span>
                            ),
                          };
                        })}
                        itens={d.cards}
                        colunaDe={(c) => c.coluna_id}
                        render={(c) => <CartaoCard c={c} />}
                        aoClicar={(c) => abrir(c.id)}
                        rotulo={(c) => `#${c.numero} ${textoLivre(c.titulo)}`}
                        aoMover={(c, destino) => {
                          // Sem permissão de editar, ou atravessando aprovação sem poder aprovar: devolve o card ao lugar e avisa.
                          if (!s.pode('tarefas.editar')) return remontar();
                          if (atravessaAprovacao(d.colunas, c.coluna_id, destino) && !s.pode('tarefas.aprovar')) {
                            toast.erro(t('ficha.aprovacaoAviso'));
                            return remontar();
                          }
                          mover.mutate({ id: c.id, coluna_id: destino });
                        }}
                      />
                    )}
                    {cardAberto && <DrawerCard key={cardAberto} id={cardAberto} colunas={d.colunas} aoFechar={() => abrir(null)} />}
                  </>
                )}
              </Estado>
            </>
          )
        }
      </Estado>
      <Drawer
        aberto={novo}
        aoFechar={() => setNovo(false)}
        largura="lg"
        titulo={t('ficha.novo')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setNovo(false)}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" type="submit" form="form-card">
              {tn('comum.salvar')}
            </Botao>
          </>
        }
      >
        {novo && quadro && (
          <FormCard
            quadro={quadro}
            usuarioId={s.usuario.id}
            aoSalvar={async (r) => {
              setNovo(false);
              toast.sucesso(t('ficha.salvo'));
              await recarregar();
              if (r.id) abrir(r.id);
            }}
          />
        )}
      </Drawer>
    </>
  );
}
